import axios from "../utils/axios";

/**
 * OTP Authentication Flow
 * =======================
 */

/**
 * Request OTP for login/register/verification
 * @param {string} channel - 'sms' or 'email'
 * @param {string} identifier - Phone number or email address
 * @param {string} purpose - 'login' | 'register' | 'verify_phone' | 'verify_email' | 'reset_password'
 * @returns {Promise} Response with cooldown_seconds and expires_at
 */
export const requestOtp = async (channel, identifier, purpose) => {
    try {
        const { data } = await axios.post('/auth/otp/request', {
            channel,
            identifier,
            purpose
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ requestOtp ~ error:", error);
        throw error;
    }
};

/**
 * Verify OTP and complete authentication/verification
 * @param {string} channel - 'sms' or 'email'
 * @param {string} identifier - Phone number or email address
 * @param {string} purpose - 'login' | 'register' | 'verify_phone' | 'verify_email' | 'reset_password'
 * @param {string} otp - 6-digit OTP code
 * @param {string} [cart_session_id] - Optional cart session ID for merging
 * @returns {Promise} Response with verified, token, user data
 */
export const verifyOtp = async (channel, identifier, purpose, otp, cart_session_id = null) => {
    try {
        const headers = {};
        if (cart_session_id) {
            headers['X-Cart-Session'] = cart_session_id;
        }

        const { data } = await axios.post('/auth/otp/verify', {
            channel,
            identifier,
            purpose,
            otp
        }, { headers });

        return data;
    } catch (error) {
        console.error("🚀 ~ verifyOtp ~ error:", error);
        throw error;
    }
};

/**
 * Resend OTP code
 * @param {string} channel - 'sms' or 'email'
 * @param {string} identifier - Phone number or email address
 * @param {string} purpose - 'login' | 'register' | 'verify_phone' | 'verify_email' | 'reset_password'
 * @returns {Promise} Response with new cooldown_seconds and expires_at
 */
export const resendOtp = async (channel, identifier, purpose) => {
    try {
        const { data } = await axios.post('/auth/otp/resend', {
            channel,
            identifier,
            purpose
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ resendOtp ~ error:", error);
        throw error;
    }
};

/**
 * Registration Flow (Two-Step Process)
 * ====================================
 */

/**
 * Step 1: Initiate registration and send OTP
 * @param {Object} userData - Registration data
 * @param {string} userData.name - Full name
 * @param {string} [userData.phone] - Phone number (required if email not provided)
 * @param {string} [userData.email] - Email address (required if phone not provided)
 * @param {string} userData.password - Password
 * @param {string} userData.password_confirmation - Password confirmation
 * @param {string} [userData.lang] - Preferred language ('ar' or 'en')
 * @returns {Promise} Response with channel, identifier, purpose, expires_at, cooldown_seconds
 */
export const initiateRegister = async (userData) => {
    try {
        const { data } = await axios.post('/auth/register', {
            name: userData.name,
            phone: userData.phone,
            email: userData.email,
            password: userData.password,
            password_confirmation: userData.password_confirmation,
            lang: userData.lang || 'ar'
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ initiateRegister ~ error:", error);
        throw error;
    }
};

/**
 * Step 2: Complete registration by verifying OTP
 * Uses verifyOtp with purpose: 'register'
 * This will create the account and auto-login the user
 */
// Use verifyOtp(channel, identifier, 'register', otp, cart_session_id)

/**
 * Phone/Email Update Flow (Authenticated)
 * ========================================
 */

/**
 * Request phone number update (sends OTP to new phone)
 * @param {string} phone - New phone number
 * @returns {Promise} Response with channel, identifier, purpose, expires_at
 */
export const requestPhoneUpdate = async (phone) => {
    try {
        const { data } = await axios.post('/user/phone/request-update', {
            phone
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ requestPhoneUpdate ~ error:", error);
        throw error;
    }
};

/**
 * Verify and update phone number
 * @param {string} phone - New phone number
 * @param {string} otp - 6-digit OTP code
 * @returns {Promise} Response confirming phone update
 */
export const verifyPhoneUpdate = async (phone, otp) => {
    try {
        const { data } = await axios.post('/user/phone/verify-update', {
            phone,
            otp
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ verifyPhoneUpdate ~ error:", error);
        throw error;
    }
};

/**
 * Request email address update (sends OTP to new email)
 * @param {string} email - New email address
 * @returns {Promise} Response with channel, identifier, purpose, expires_at
 */
export const requestEmailUpdate = async (email) => {
    try {
        const { data } = await axios.post('/user/email/request-update', {
            email
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ requestEmailUpdate ~ error:", error);
        throw error;
    }
};

/**
 * Verify and update email address
 * @param {string} email - New email address
 * @param {string} otp - 6-digit OTP code
 * @returns {Promise} Response confirming email update
 */
export const verifyEmailUpdate = async (email, otp) => {
    try {
        const { data } = await axios.post('/user/email/verify-update', {
            email,
            otp
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ verifyEmailUpdate ~ error:", error);
        throw error;
    }
};
/**
 * Complete password reset by verifying OTP and setting new password
 * @param {Object} resetData - { channel, identifier, otp, password, password_confirmation }
 * @returns {Promise} Response confirming password reset
 */
export const resetPassword = async (resetData) => {
    try {
        const { data } = await axios.post('/auth/reset-password', {
            channel: resetData.channel,
            identifier: resetData.identifier,
            otp: resetData.otp,
            password: resetData.password,
            password_confirmation: resetData.password_confirmation
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ resetPassword ~ error:", error);
        throw error;
    }
};
